import Vue from "vue"

export const WS_STATUS = {
    CONNECTING: "Connexion...",
    CONNECTED: "Connecté",
    NOT_CONNECTED: "Non connecté",
    ERROR: "Erreur"
}

const WS_NATIVE_EVENTS = {
    OPEN: "open",
    MESSAGE: "message",
    ERROR: "error",
    CLOSE: "close"
}

const ViewerEvents = {
    JOIN: "join",
    JOINED: "joined",
    LEAVE: "leave",
    VIEWERS_UPDATED: "viewers updated",
    PING: "ping",
    PONG: "pong",
    ERROR: "error",
    CHECK_MAP: "check map",
    CHECK_MAP_RESULT: "check map result"
}

const SERVER_TIMEOUT_MS = 60 * 1000
const CONNECT_TIMEOUT_MS = 10 * 1000


// [N] Debug prints ----------
const CAN_PRINT = false // [N] Toggle this
const LOG_PREFIX = "[webSocketService]"

function debugLog(...args) {
    if (!CAN_PRINT) { return }
    console.log(LOG_PREFIX, ...args)
}

function debugWarn(...args) {
    if (!CAN_PRINT) { return }
    console.warn(LOG_PREFIX, ...args)
}
// ---------------------------


export function createWebSocketService() {
    // Reactive + shared
    const state = Vue.observable({
        socketReadyState: WebSocket.CLOSED,
        status: WS_STATUS.NOT_CONNECTED,
        joined: false,
        resourceKey: null,
        viewers: [],
        viewersUpdateVersion: 0,
        viewersMapStatus: null,
        viewersMapStatusPending: false
    })

    let socket = null
    let shouldReconnect = false
    let reconnectTimeout = null
    let connectTimeout = null // [N] ne pas laisser un onglet bloqué en 'connecting...'
    let serverSilenceTimeout = null
    let networkListenersInstalled = false


    let joiningResourceKey = null // avant de recevoir confirmation pour devenir l'active
    let activeResourceKey = null  // la ressourceKey validée par le serveur

    function socketIsReady() {
        return socket && state.socketReadyState === WebSocket.OPEN
    }

    function isStillCurrentSocket(newSocket) {
        return socket === newSocket
    }

    function socketIsConnecting() {
        return socket && socket.readyState === WebSocket.CONNECTING
    }

    function joiningPayload(resourceKey) {
        const payload = {
            event: ViewerEvents.JOIN,
            resourceKey
        }
        return JSON.stringify(payload)
    }

    function leavingPayload() {
        const payload = { event: ViewerEvents.LEAVE}
        return JSON.stringify(payload)
    }

    function pongPayload() {
        const payload = {
            event: ViewerEvents.PONG,
        }
        return JSON.stringify(payload)
    }

    function pingPayload() {
        const payload = {
            event: ViewerEvents.PING,
            resourceKey: activeResourceKey
        }
        return JSON.stringify(payload)
    }

    function checkMapPayload() {
        const payload = {
            event: ViewerEvents.CHECK_MAP,
            resourceKey: activeResourceKey
        }
        return JSON.stringify(payload)
    }

    function connect() {
        installNetworkListeners()
        if (socketIsReady() || socketIsConnecting()) { return }

        const socketUrl = "/api/websocket/viewer"

        shouldReconnect = true
        state.status = WS_STATUS.CONNECTING
        state.socketReadyState = WebSocket.CONNECTING

        let newSocket

        try {
            newSocket = new WebSocket(socketUrl)
        } catch (error) {
            state.status = WS_STATUS.ERROR
            state.socketReadyState = WebSocket.CLOSED

            console.error("Création WebSocket impossible", { error })

            scheduleReconnect()
            return
        }

        socket = newSocket

        clearConnectTimeout()
        connectTimeout = setTimeout(() => {
            if (!isStillCurrentSocket(newSocket)) { return }
            if (newSocket.readyState !== WebSocket.CONNECTING) { return }

            debugWarn("Timeout du handshake WebSocket", {
                visibility: document.visibilityState
            })

            invalidateSocket("connection timeout")
        }, CONNECT_TIMEOUT_MS)

        newSocket.addEventListener(WS_NATIVE_EVENTS.ERROR, event => {
            if (!isStillCurrentSocket(newSocket)) { return }
            state.status = WS_STATUS.ERROR
            state.socketReadyState = newSocket.readyState

            console.error("WebSocket error:", event)
        })

        newSocket.addEventListener(WS_NATIVE_EVENTS.OPEN, () => {
            if (!isStillCurrentSocket(newSocket)) { return }
            state.status = WS_STATUS.CONNECTED
            state.socketReadyState = WebSocket.OPEN

            clearConnectTimeout()
            syncResourceKey()
        })

        newSocket.addEventListener(WS_NATIVE_EVENTS.CLOSE, event => {
            if (!isStillCurrentSocket(newSocket)) { return }
            state.status = WS_STATUS.NOT_CONNECTED
            state.socketReadyState = WebSocket.CLOSED
            state.joined = false
            state.viewers = []
            state.viewersMapStatusPending = false

            socket = null
            joiningResourceKey = null
            activeResourceKey = null

            scheduleReconnect()
            clearServerSilenceTimeout()
            clearConnectTimeout()
        })

        newSocket.addEventListener(WS_NATIVE_EVENTS.MESSAGE, event => {
            if (!isStillCurrentSocket(newSocket)) { return }
            handleMessage(event)
        })
    }

    function installNetworkListeners() {
        if (networkListenersInstalled) { return }
        window.addEventListener("offline", handleBrowserOffline)
        window.addEventListener("online", handleBrowserOnline)
        networkListenersInstalled = true
    }

    function removeNetworkListeners() {
        if (!networkListenersInstalled) { return }
        window.removeEventListener("offline", handleBrowserOffline)
        window.removeEventListener("online", handleBrowserOnline)
        networkListenersInstalled = false
    }

    function handleMessage(event) {
        debugLog("WS message brut :", event.data)
        let payload = null

        try {
            payload = JSON.parse(event.data)
        } catch (error) {
            console.error("[webSocketService] Message WebSocket non JSON :", event.data)
            return
        }

        debugLog("WS message parsé :", payload)

        if (payload.event === ViewerEvents.JOINED) {
            state.joined = true
            activeResourceKey = joiningResourceKey
            joiningResourceKey = null
            debugLog("JOINED :", { activeResourceKey, requestedResourceKey: state.resourceKey})
            syncResourceKey()
            startServerSilenceTimeout()
        }

        if (payload.event === ViewerEvents.PING) {
            startServerSilenceTimeout()
            sendPong()
        }

        if (payload.event === ViewerEvents.VIEWERS_UPDATED) {
            const debug = false
            const fakeViewers = [
                {
                    firstname: "Martin",
                    lastname: "Picadillo",
                    userId: 15,
                },
                {
                    firstname: "Gontran",
                    lastname: "De la Rosa",
                    userId: 16,
                },
                {
                    firstname: "Géraldine",
                    lastname: "Bazooka",
                    userId: 17,
                },
                {
                    firstname: "Arthur",
                    lastname: "Trompette",
                    userId: 22,
                },
                {
                    firstname: "Natacha",
                    lastname: "Mallo",
                    userId: 36,
                },
                {
                    firstname: "Sophie",
                    lastname: "Tucker",
                    userId: 57,
                },
            ]

            if (debug) {
                state.viewers = [...fakeViewers, ...payload.viewers] || []
            } else {
                state.viewers = payload.viewers || []
            }

            state.viewersUpdateVersion += 1

            debugLog("VIEWERS UPDATED :", payload)
        }

        if (payload.event === ViewerEvents.CHECK_MAP_RESULT) {
            handleCheckMapResult(payload)
        }

        if (payload.event === ViewerEvents.ERROR) {
            console.error("[webSocketService] VIEWER ERROR :", payload)
        }
    }

    function handleCheckMapResult(payload) {
        const data = payload.data

        if (!data || typeof data !== "object" || Array.isArray(data)) {
            state.viewersMapStatusPending = false
            console.error("[webSocketService] CHECK_MAP_RESULT invalide :", payload)
            return
        }

        state.viewersMapStatus = {
            ...data,
            consistencyErrors: Array.isArray(data.consistencyErrors) ? data.consistencyErrors : [],
            viewers: Array.isArray(data.viewers) ? data.viewers : []
        }

        state.viewersMapStatusPending = false
        debugLog("CHECK_MAP_RESULT :", data)
    }

    function sendJoin() {
        if (!socketIsReady() || !state.resourceKey || joiningResourceKey) { return }

        joiningResourceKey = state.resourceKey
        debugLog("JOIN envoyé :", joiningResourceKey)
        socket.send(joiningPayload(joiningResourceKey))
    }

    function sendLeave() {
        if (!socketIsReady()) { return }
        if (!state.joined && !activeResourceKey) { return }
        const leavingResourceKey = activeResourceKey
        debugLog("LEAVE envoyé :", leavingResourceKey)
        socket.send(leavingPayload())

        state.joined = false
        state.viewers = []
        activeResourceKey = null
        clearServerSilenceTimeout()
    }

    function setResourceKey(resourceKey) {
        if (!resourceKey || resourceKey === state.resourceKey) { return }

        debugLog("new resourceKey :", resourceKey)
        state.resourceKey = resourceKey
        state.viewers = []
        syncResourceKey()
    }

    function syncResourceKey() {
        if (!socketIsReady()) { return }
        if (!state.resourceKey) { // On switch sur une route non-surveillée
            if (joiningResourceKey) { return }
            sendLeave()
            return
        }
        if (joiningResourceKey) { return }
        if (state.joined && activeResourceKey === state.resourceKey) {
            return
        }

        sendJoin()
    }

    function startServerSilenceTimeout() {
        clearServerSilenceTimeout()
        if (!state.joined) { return }

        serverSilenceTimeout = setTimeout(() => {
            debugWarn("Aucun PING serveur reçu, socket considérée obsolète")
            invalidateSocket("server ping timeout")
        }, SERVER_TIMEOUT_MS)
    }

    function clearServerSilenceTimeout() {
        if (!serverSilenceTimeout) {
            return
        }

        clearTimeout(serverSilenceTimeout)
        serverSilenceTimeout = null
    }

    function clearConnectTimeout() {
        if (!connectTimeout) { return }

        clearTimeout(connectTimeout)
        connectTimeout = null
    }

    function scheduleReconnect() {
        if (!shouldReconnect || reconnectTimeout) { return }

        reconnectTimeout = setTimeout(() => {
            reconnectTimeout = null
            connect()
        }, 3000)
    }

    function sendPing() {
        if (!socketIsReady()) { return }

        debugLog("PING envoyé (with resourceKey=", state.resourceKey, ")")
        socket.send(pingPayload())
    }

    function sendPong() {
        if (!socketIsReady()) { return }

        debugLog("PONG envoyé (with resourceKey=", state.resourceKey, ")")
        socket.send(pongPayload())
    }

    function sendCheckMap() {
        if (!socketIsReady() || state.viewersMapStatusPending) { return }
        state.viewersMapStatusPending = true
        debugLog("CHECK MAP envoyé (resourceKey=", state.resourceKey, ")")
        socket.send(checkMapPayload())
    }

    function clearReconnectTimeout() {
        if (!reconnectTimeout) { return }

        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
    }

    function clearResourceKey() {
        if (!state.resourceKey && !state.joined && !joiningResourceKey && !activeResourceKey) {
            return
        }

        debugLog("Suppression de la resourceKey :", state.resourceKey)
        state.resourceKey = null
        state.viewers = []
        syncResourceKey()
    }

    // Unexpected close -> will trigger scheduleReconnect()
    function closeSocket() {
        if (!socket) { return }
        if (socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED) {
            return
        }

        state.status = WS_STATUS.NOT_CONNECTED
        state.socketReadyState = WebSocket.CLOSING

        socket.close()
    }

    // Closed by timer (no ping from server since SERVER_TIMEOUT_MS) -> will trigger scheduleReconnect()
    function invalidateSocket(reason) {
        const invalidSocket = socket

        if (!invalidSocket) { return }
        debugWarn("   Socket invalidée :", reason)

        socket = null

        state.status = WS_STATUS.NOT_CONNECTED
        state.socketReadyState = WebSocket.CLOSED
        state.joined = false
        state.viewers = []
        state.viewersMapStatusPending = false

        joiningResourceKey = null
        activeResourceKey = null

        clearServerSilenceTimeout()
        clearConnectTimeout()

        try {
            invalidSocket.close()
        } catch (error) {
            debugWarn("   Impossible de fermer l'ancienne socket", error)
        }

        if (shouldReconnect && navigator.onLine !== false) {
            scheduleReconnect()
        }
    }

    function handleBrowserOffline() {
        debugWarn("   Navigateur hors ligne")
        invalidateSocket("browser offline")
    }

    function handleBrowserOnline() {
        debugLog("Navigateur de nouveau en ligne")
        invalidateSocket("browser online") // connect() will create a new socket
        if (shouldReconnect) {
            clearReconnectTimeout()
            connect()
        }
    }

    // Expected close -> no trigger reconnect
    function disconnect() {
        shouldReconnect = false
        clearReconnectTimeout()
        closeSocket()
        removeNetworkListeners()
        clearServerSilenceTimeout()
        clearConnectTimeout()
    }

    return {
        state,
        connect,
        disconnect,
        setResourceKey,
        clearResourceKey,
        sendPing,
        sendPong,
        sendCheckMap
    }
}